import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';

const descriptionBodyStyle = (colors, style) => ({
  fontSize: '0.75rem',
  color: colors.textMuted,
  lineHeight: 1.3,
  whiteSpace: 'pre-line',
  margin: '6px 0 0',
  paddingBottom: 10,
  ...style,
});

/**
 * Collapsed-by-default copy with a chevron toggle for rule cards and section headers.
 */
export function useExpandableDescription(text, { style } = {}) {
  const [expanded, setExpanded] = useState(false);
  const { colors } = useTheme();

  if (!text) {
    return { toggle: null, body: null };
  }

  const toggle = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      aria-label={expanded ? 'Hide description' : 'Show description'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        background: 'none',
        color: colors.textMuted,
        cursor: 'pointer',
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
    </button>
  );

  const body = expanded ? (
    <p style={descriptionBodyStyle(colors, style)}>{text}</p>
  ) : null;

  return { toggle, body };
}

/** Title row with chevron immediately to the right of the title. */
export function InlineExpandableDescription({ title, text, titleStyle, style }) {
  const { toggle, body } = useExpandableDescription(text, { style });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={titleStyle}>{title}</div>
        {toggle}
      </div>
      {body}
    </>
  );
}

/** Standalone block when toggle and body stay together (e.g. checkbox sublabels). */
const ExpandableDescription = ({ text, style, title, titleStyle }) => {
  const { toggle, body } = useExpandableDescription(text, { style });

  if (title) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={titleStyle}>{title}</div>
          {toggle}
        </div>
        {body}
      </>
    );
  }

  if (!text) return null;

  return (
    <div>
      {toggle}
      {body}
    </div>
  );
};

export default ExpandableDescription;
